class BackInStockForm extends HTMLElement {
  static KLAVIYO_CLIENT_API = 'https://a.klaviyo.com/client';
  static KLAVIYO_REVISION = '2025-01-15';
  static EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  static ACTIVE_CLASS = 'back-in-stock--active';
  static ATC_LOADING_CLASS = 'loading';

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('[data-bis-form]');
    this.emailInput = this.querySelector('[data-bis-email]');
    this.emailError = this.querySelector('[data-bis-email-error]');
    this.consentInput = this.querySelector('[data-bis-consent]');
    this.submitButton = this.querySelector('[data-bis-submit]');
    this.productForm = this.closest('product-form');
    this.buttonWrapper = this.closest('.product-form__btn-wrapper');

    this.addEventListener('click', this.onClick);
    this.emailInput?.addEventListener('keydown', this.onEmailKeydown);
    this.dialog?.addEventListener('click', this.onBackdropClick);
    this.dialog?.addEventListener('close', this.onDialogClose);
    this.productForm?.addEventListener('change', this.scheduleSync);

    if (this.buttonWrapper) {
      this.observer = new MutationObserver(this.scheduleSync);
      this.observer.observe(this.buttonWrapper, {
        attributes: true,
        attributeFilter: ['disabled', 'class'],
        childList: true,
        subtree: true,
      });
    }

    this.syncState();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.removeEventListener('click', this.onClick);
    this.emailInput?.removeEventListener('keydown', this.onEmailKeydown);
    this.dialog?.removeEventListener('click', this.onBackdropClick);
    this.dialog?.removeEventListener('close', this.onDialogClose);
    this.productForm?.removeEventListener('change', this.scheduleSync);
  }

  get atcButton() {
    return this.buttonWrapper?.querySelector('.product-form__btn') || null;
  }

  get variantId() {
    return this.productForm?.querySelector('input[name="id"]')?.value?.trim() || '';
  }

  scheduleSync = () => {
    if (this.syncPending) return;
    this.syncPending = true;
    requestAnimationFrame(() => {
      this.syncPending = false;
      this.syncState();
    });
  };

  syncState = () => {
    const atc = this.atcButton;
    const loading = Boolean(atc?.classList.contains(BackInStockForm.ATC_LOADING_CLASS));
    if (loading) return;
    const soldOut = Boolean(atc?.disabled) && this.variantId !== '';
    this.classList.toggle(BackInStockForm.ACTIVE_CLASS, soldOut);
    if (!soldOut && this.dialog?.open) this.dialog.close();
  };

  onClick = (event) => {
    if (event.target.closest('[data-bis-open]')) {
      this.open();
    } else if (event.target.closest('[data-bis-submit]')) {
      this.submit();
    } else if (event.target.closest('[data-bis-close]')) {
      this.dialog?.close();
    } else if (event.target.closest('[data-bis-retry]')) {
      this.showView('form');
      this.emailInput?.focus();
    }
  };

  onEmailKeydown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.submit();
  };

  onBackdropClick = (event) => {
    if (event.target === this.dialog) this.dialog.close();
  };

  onDialogClose = () => {
    this.showView('form');
    if (this.emailError) this.emailError.hidden = true;
    this.emailInput?.removeAttribute('aria-invalid');
  };

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.showView('form');
    this.dialog.showModal();
    this.emailInput?.focus();
    this.track('Viewed Back in Stock Form');
  }

  showView(name) {
    this.querySelectorAll('[data-bis-view]').forEach((view) => {
      view.hidden = view.dataset.bisView !== name;
    });
  }

  setPending(pending) {
    if (!this.submitButton) return;
    this.submitButton.disabled = pending;
    this.submitButton.setAttribute('aria-busy', String(pending));
  }

  validateEmail() {
    const email = this.emailInput?.value.trim() || '';
    const valid = BackInStockForm.EMAIL_PATTERN.test(email);
    if (this.emailError) this.emailError.hidden = valid;
    this.emailInput?.setAttribute('aria-invalid', String(!valid));
    return valid ? email : null;
  }

  async submit() {
    if (this.submitButton?.disabled) return;
    const email = this.validateEmail();
    if (!email) {
      this.emailInput?.focus();
      return;
    }
    const variantId = this.variantId;
    if (!variantId) {
      this.showView('error');
      return;
    }

    this.setPending(true);
    try {
      await this.subscribeBackInStock(email, variantId);
      if (this.consentInput?.checked && this.dataset.listId) {
        await this.subscribeMarketing(email).catch((error) => {
          console.warn('[back-in-stock] marketing consent failed', error);
        });
      }
      this.track('Submitted Back in Stock Form', { Email: email }, email);
      this.showView('success');
      this.querySelector('[data-bis-view="success"] [data-bis-close]')?.focus();
    } catch (error) {
      console.error('[back-in-stock] subscription failed', error);
      this.showView('error');
    } finally {
      this.setPending(false);
    }
  }

  post(path, body) {
    const url = `${BackInStockForm.KLAVIYO_CLIENT_API}/${path}/?company_id=${encodeURIComponent(this.dataset.companyId || '')}`;
    return fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'content-type': 'application/vnd.api+json',
        accept: 'application/vnd.api+json',
        revision: BackInStockForm.KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error(`Klaviyo ${path} responded ${response.status}`);
      return response;
    });
  }

  subscribeBackInStock(email, variantId) {
    return this.post('back-in-stock-subscriptions', {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email } } },
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
        },
      },
    });
  }

  subscribeMarketing(email) {
    return this.post('subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in Stock form',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                properties: { 'Storefront Language': this.dataset.locale },
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: this.dataset.listId } } },
      },
    });
  }

  track(eventName, extra = {}, email) {
    const klaviyo = window.klaviyo;
    if (!klaviyo || typeof klaviyo.push !== 'function') return;
    try {
      if (email) klaviyo.push(['identify', { email, 'Storefront Language': this.dataset.locale }]);
      klaviyo.push([
        'track',
        eventName,
        {
          ProductID: this.dataset.productId,
          ProductName: this.dataset.productTitle,
          VariantID: this.variantId,
          Locale: this.dataset.locale,
          ...extra,
        },
      ]);
    } catch (error) {
      console.warn('[back-in-stock] tracking failed', error);
    }
  }
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}
